import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { useHtzConfirmationPutMutation } from "@/services/backend/htz/confirmations/api/confirmation-api";
import { newConfirmation } from "@/services/backend/htz/confirmations/types/confirmation";
import { ReactNode, useEffect, useState } from "react";
import { Slot } from "radix-ui";
import { Button } from "@/shared/components/ui/button";
import { Plus } from "lucide-react";
import t from "@/lang/lang";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";
import { BranchSelectFrom } from "@/services/backend/branch/components/branch-select-from";
import { ServiceObjectSelectFrom } from "@/services/backend/customers/components/service-object-select-from";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import {
  CommonDialogFooter,
  CommonDialogHeader,
} from "@/shared/components/dialog/dialog-common";
import { toast } from "sonner";

interface Props {
  processId: string;
  onCreated: (confirmationId: string) => void;
  trigger?: ReactNode;
}

export function CreateConfirmationButton({
  processId,
  onCreated,
  trigger,
}: Props) {
  const [open, setOpen] = useState(false);
  const { data: process } = useProcessesProcessShowQuery({ id: processId });
  const [put, { isLoading, error, reset }] = useHtzConfirmationPutMutation();

  const branches = process?.branches ?? [];
  const serviceObjects = process?.serviceObjects ?? [];
  const [branchId, setBranchId] = useState(branches[0] ?? "");
  const [serviceObjectId, setServiceObjectId] = useState(
    serviceObjects[0] ?? "",
  );

  useEffect(() => {
    if (error) {
      toast(...parseRTKQueryErrorToSonner(error));
      reset();
    }
  }, [error, reset]);

  const disabled =
    isLoading || !process || branchId === "" || serviceObjectId === "";

  const doCreate = () => {
    if (disabled) {
      return;
    }

    put(newConfirmation(process, branchId, serviceObjectId))
      .unwrap()
      .then((c) => {
        setOpen(false);
        onCreated(c.id);
        reset();
      });
  };

  if (branches.length === 1 && serviceObjects.length === 1) {
    if (trigger) {
      return <Slot.Root onClick={doCreate}>{trigger}</Slot.Root>;
    }
    return (
      <Button onClick={doCreate} disabled={disabled} size="sm">
        <Plus className="mr-2 h-5 w-5" />
        <span>{t("Auftragsbestätigung")}</span>
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button disabled={disabled} size="sm">
            <Plus className="mr-2 h-5 w-5" />
            <span>{t("Auftragsbestätigung")}</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <CommonDialogHeader title={t("Auftragsbestätigung erstellen")} />
        {branches.length !== 1 && (
          <Field>
            <FieldLabel>{t("Niederlassung")}</FieldLabel>
            <BranchSelectFrom
              branchIds={branches}
              value={branchId}
              onValueChange={setBranchId}
            />
          </Field>
        )}
        {serviceObjects.length !== 1 && (
          <Field>
            <FieldLabel>{t("Service Objekt")}</FieldLabel>
            <ServiceObjectSelectFrom
              serviceObjectIds={serviceObjects}
              customerId={process?.customerId}
              value={serviceObjectId}
              onValueChange={setServiceObjectId}
            />
          </Field>
        )}
        <CommonDialogFooter
          isLoading={isLoading}
          disabled={disabled}
          onClickAction={doCreate}
        />
      </DialogContent>
    </Dialog>
  );
}
